'use client';

import React, { useRef, useLayoutEffect, useState } from 'react';
import { DEFAULT_HIGHLIGHT_COLOR, isGradientColorway } from '../consts';
import type { Color } from 'libs/contentful/types';
import { cn, getColorway } from 'utils/styles';

function useHighlightColumnRect(ref: React.RefObject<HTMLDivElement | null>, highlightedColumnIndex: number) {
  const [rect, setRect] = useState<{ top: number; left: number; width: number; height: number } | null>(null);

  useLayoutEffect(() => {
    const measure = () => {
      const wrapper = ref.current;
      const table = wrapper?.querySelector('table');
      const cell = table?.querySelector<HTMLElement>('[data-highlighted="true"]');
      if (!wrapper || !table || !cell) return setRect(null);
      const wrapperRect = wrapper.getBoundingClientRect();
      const tableRect = table.getBoundingClientRect();
      const cellRect = cell.getBoundingClientRect();
      setRect({
        top: tableRect.top - wrapperRect.top,
        left: cellRect.left - wrapperRect.left,
        width: cellRect.width,
        height: tableRect.height,
      });
    };
    measure();
    if (!ref.current) return;
    const obs = new ResizeObserver(measure);
    obs.observe(ref.current);
    return () => obs.disconnect();
  }, [highlightedColumnIndex, ref]);

  return rect;
}

interface TableWrapperProps {
  children: React.ReactNode;
  highlightedColumnIndex: number;
  highlightColor: Color | undefined;
}

export const TableWrapper = ({ children, highlightedColumnIndex, highlightColor }: TableWrapperProps) => {
  const highlightColorwayClass = getColorway(highlightColor ?? DEFAULT_HIGHLIGHT_COLOR);
  const isGradient = isGradientColorway(highlightColorwayClass);

  const ref = useRef<HTMLDivElement>(null);
  const rect = useHighlightColumnRect(ref, isGradient ? highlightedColumnIndex : -1);

  return (
    <div ref={ref} className="relative">
      {rect && isGradient && (
        <div
          aria-hidden
          className={cn('absolute rounded-lg pointer-events-none', highlightColorwayClass)}
          style={{
            top: rect.top,
            left: rect.left,
            width: rect.width,
            height: rect.height,
          }}
        />
      )}
      {children}
    </div>
  );
};
